import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "./Brand";

/** Last resort for a render-time crash (WP-03.1): without this, one thrown error in any page unmounts the whole
 * tree and the person is left on a blank white screen with no way back. Data-fetch failures are handled per panel
 * by `ErrorBox` — this only catches what those never see, i.e. a bug in rendering itself. */
function Fallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F6F8FA] p-6 text-brand-charcoal">
      <div className="max-w-md rounded-lg border border-brand-grey-light bg-white p-6 text-center shadow-sm">
        <BrandLogo variant="mark" className="mx-auto h-10" />
        <h1 className="mt-4 font-heading text-lg font-semibold">{t("common.crashTitle")}</h1>
        <p className="mt-2 text-sm text-brand-grey">{t("common.crashBody")}</p>
        <div className="mt-4 flex justify-center gap-2">
          <button onClick={onReset} className="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:bg-brand-deep">
            {t("common.crashRetry")}
          </button>
          <a href="/" className="rounded-md border border-brand-grey-light px-3 py-2 text-sm font-semibold hover:bg-[#F6F8FA]">
            {t("common.crashHome")}
          </a>
        </div>
      </div>
    </main>
  );
}

export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("unhandled render error", error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <Fallback onReset={() => this.setState({ failed: false })} />;
  }
}
